/**
 * Feature-comparison matrix (`featureCategories` below) mirrors the public
 * pricing page (prodaff.com/pricing-plan/), captured directly from its
 * rendered comparison table, including the real tooltip copy sourced from
 * the page's own markup (each feature/sub-feature's `.tip` hover text).
 *
 * Prices, tiers and billing cycles live in `src/config/pricing.js`; this file
 * only holds feature availability.
 */

/**
 * Feature comparison matrix: category -> feature -> optional sub-features.
 * `availability` maps each plan tier (see PLAN_TIERS) to a boolean (true = checkmark,
 * false = dash/not included). No plan shows a partial/"x" state in the
 * source table — only checkmark or dash.
 */
export const featureCategories = [
  {
    key: 'time-tracking',
    label: 'Time Tracking',
    features: [
      {
        key: 'basic-time-tracking',
        label: 'Basic Time Tracking',
        tooltip:
          'Log time manually or via start/stop timers against tasks and projects.',
        availability: { starter: true, grow: true, enterprise: true },
        subFeatures: [
          {
            key: 'manual-time-entry',
            label: 'Manual time entry',
            tooltip:
              'Add time logs by typing in hours and minutes directly — no timer needed. Ideal for logging time after the fact.',
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'timer-start-stop',
            label: 'Timer start / stop',
            tooltip:
              'Start a live timer when you begin a task and stop it when done. Time is recorded automatically to the second.',
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'time-rounding-rules',
            label: 'Time rounding rules',
            tooltip:
              'Automatically round time entries to the nearest 5, 10, or 15 minutes for cleaner timesheets.',
            availability: { starter: true, grow: true, enterprise: true },
          },
        ],
      },
      {
        key: 'automatic-time-capture',
        label: 'Automatic Time Capture',
        tooltip:
          'Automatically records time spent on apps and websites without manual input.',
        availability: { starter: true, grow: true, enterprise: true },
        subFeatures: [
          {
            key: 'app-website-detection',
            label: 'App & website detection',
            tooltip:
              'Detects and logs which applications and websites you use throughout the day, mapped to your active tasks.',
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'background-capture',
            label: 'Background capture',
            tooltip:
              'Runs silently in the background without interrupting your workflow — no manual start or stop required.',
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'silent-stealth-mode',
            label: 'Silent / stealth mode',
            tooltip:
              "Captures activity invisibly without any visible interface or notifications on the user's screen.",
            availability: { starter: true, grow: true, enterprise: true },
          },
        ],
      },
      {
        key: 'idle-time-detection',
        label: 'Idle Time Detection',
        tooltip:
          'Identifies inactive periods so managers get accurate active-time data.',
        availability: { starter: true, grow: true, enterprise: true },
        subFeatures: [
          {
            key: 'configurable-idle-threshold',
            label: 'Configurable idle threshold',
            tooltip:
              'Set the number of idle minutes before the system flags a period as inactive (e.g. after 5 or 10 minutes of no input).',
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'idle-prompts-for-users',
            label: 'Idle prompts for users',
            tooltip:
              'When idle time is detected, users receive a prompt asking what they were doing so time can be reassigned accurately.',
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'auto-discard-idle-time',
            label: 'Auto-discard idle time',
            tooltip:
              'Idle periods can be automatically removed from tracked time so only genuine working time is recorded.',
            availability: { starter: true, grow: true, enterprise: true },
          },
        ],
      },
      {
        key: 'offline-time-tracking',
        label: 'Offline Time Tracking',
        tooltip:
          'Continues tracking without internet; auto-syncs when back online.',
        availability: { starter: false, grow: true, enterprise: true },
        subFeatures: [
          {
            key: 'local-cache-mode',
            label: 'Local cache mode',
            tooltip:
              'All time data is saved locally on the device when internet is unavailable, ensuring no data is lost.',
            availability: { starter: false, grow: true, enterprise: true },
          },
          {
            key: 'auto-sync-on-reconnect',
            label: 'Auto-sync on reconnect',
            tooltip:
              'As soon as the device reconnects, all cached time entries are automatically uploaded to the server.',
            availability: { starter: false, grow: true, enterprise: true },
          },
        ],
      },
      {
        key: 'desktop-apps',
        label: 'Desktop Apps',
        tooltip:
          'Native Windows and macOS apps for seamless background tracking.',
        availability: { starter: true, grow: true, enterprise: true },
        subFeatures: [
          {
            key: 'windows-client',
            label: 'Windows client',
            tooltip:
              'A lightweight native app for Windows that runs in the system tray and tracks time and activity automatically.',
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'macos-client',
            label: 'macOS client',
            tooltip:
              'A native macOS app that integrates with the menu bar for unobtrusive time and activity tracking.',
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'auto-launch-on-login',
            label: 'Auto-launch on login',
            tooltip:
              'The desktop app starts automatically when the computer boots so tracking begins immediately.',
            availability: { starter: true, grow: true, enterprise: true },
          },
        ],
      },
    ],
  },
  {
    key: 'monitoring-activity',
    label: 'Monitoring & Activity',
    features: [
      {
        key: 'screenshots-activity-levels',
        label: 'Screenshots & Activity Levels',
        tooltip:
          'Periodic screenshots and mouse/keystroke activity percentages.',
        availability: { starter: true, grow: true, enterprise: true },
        subFeatures: [
          {
            key: 'configurable-screenshot-frequency',
            label: 'Configurable screenshot frequency',
            tooltip:
              'Admins set how often screenshots are captured — every 5, 10, or 30 minutes — or turn them off per user or team.',
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'activity-percentage-score',
            label: 'Activity percentage score',
            tooltip:
              'Each time interval is scored as a percentage of active keyboard and mouse usage, giving a clear productivity indicator.',
            availability: { starter: true, grow: true, enterprise: true },
          },
        ],
      },
      {
        key: 'website-app-tracking',
        label: 'Website & App Tracking',
        tooltip: 'Records which websites and apps are used during work hours.',
        availability: { starter: false, grow: true, enterprise: true },
        subFeatures: [
          {
            key: 'category-based-classification',
            label: 'Category-based classification',
            tooltip:
              'Websites and apps are automatically sorted into categories (Communication, Social Media, Development) for easy analysis.',
            availability: { starter: false, grow: true, enterprise: true },
          },
          {
            key: 'productive-vs-unproductive-labelling',
            label: 'Productive vs. unproductive labelling',
            tooltip:
              'Each site or app is flagged productive, unproductive, or neutral — giving managers an instant productivity score per user.',
            availability: { starter: false, grow: true, enterprise: true },
          },
          {
            key: 'blocked-site-alerts',
            label: 'Blocked-site alerts',
            tooltip:
              'Managers receive alerts when users visit sites designated as off-limits during work hours.',
            availability: { starter: false, grow: true, enterprise: true },
          },
        ],
      },
      {
        key: 'attendance-tracking',
        label: 'Attendance Tracking',
        tooltip: 'Logs daily check-in/out, late arrivals, and absences.',
        availability: { starter: true, grow: true, enterprise: true },
        subFeatures: [
          {
            key: 'shift-based-attendance',
            label: 'Shift-based attendance',
            tooltip:
              'Attendance is measured against predefined shifts so tardiness and early departures are automatically flagged.',
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'late-absence-alerts',
            label: 'Late & absence alerts',
            tooltip:
              'Managers receive real-time notifications when a team member checks in late or fails to check in at all.',
            availability: { starter: true, grow: true, enterprise: true },
          },
        ],
      },
    ],
  },
  {
    key: 'projects-tasks',
    label: 'Projects & Tasks',
    features: [
      {
        key: 'task-management',
        label: 'Task Management',
        tooltip:
          'Create, assign, and prioritise tasks with due dates and status labels.',
        availability: { starter: true, grow: true, enterprise: true },
        subFeatures: [
          {
            key: 'task-creation-assignment',
            label: 'Task creation & assignment',
            tooltip:
              'Create tasks in seconds and assign them to one or multiple team members with a clear owner and due date.',
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'priority-levels',
            label: 'Priority levels',
            tooltip:
              'Mark tasks as Low, Medium, High, or Urgent so teams always know what to tackle first.',
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'due-date-tracking',
            label: 'Due date tracking',
            tooltip:
              'Set and track deadlines per task; overdue tasks are automatically highlighted in dashboards and reports.',
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'status-labels',
            label: 'Status labels',
            tooltip:
              'Use built-in statuses (To Do, In Progress, In Review, Done) to keep everyone aligned on task progress.',
            availability: { starter: true, grow: true, enterprise: true },
          },
        ],
      },
      {
        key: 'project-management',
        label: 'Project Management',
        tooltip:
          'Group tasks into projects, set budgets, and track overall progress.',
        availability: { starter: true, grow: true, enterprise: true },
        subFeatures: [
          {
            key: 'project-budgets',
            label: 'Project budgets',
            tooltip:
              'Set a time or cost budget per project and track burn rate in real time against actual logged hours.',
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'milestone-tracking',
            label: 'Milestone tracking',
            tooltip:
              'Break projects into key milestones with target dates so progress can be measured at a high level.',
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'project-health-indicators',
            label: 'Project health indicators',
            tooltip:
              'At-a-glance colour-coded health scores (On Track, At Risk, Overdue) based on budget and timeline data.',
            availability: { starter: true, grow: true, enterprise: true },
          },
        ],
      },
      {
        key: 'custom-task-workflows',
        label: 'Custom Task Workflows',
        tooltip: 'Define custom statuses and workflow automation rules.',
        availability: { starter: false, grow: true, enterprise: true },
        subFeatures: [
          {
            key: 'custom-status-columns',
            label: 'Custom status columns',
            tooltip:
              'Add your own workflow stages (e.g. Awaiting Client, QA Review) to match exactly how your team works.',
            availability: { starter: false, grow: true, enterprise: true },
          },
          {
            key: 'workflow-automation',
            label: 'Workflow automation',
            tooltip:
              'Set rules like "when task moves to Done, notify the manager" to reduce manual follow-up.',
            availability: { starter: false, grow: true, enterprise: true },
          },
          {
            key: 'recurring-tasks',
            label: 'Recurring tasks',
            tooltip:
              'Schedule tasks to repeat daily, weekly, or monthly so routine work is always created automatically.',
            availability: { starter: false, grow: true, enterprise: true },
          },
        ],
      },
      {
        key: 'billable-non-billable-time',
        label: 'Billable & Non-Billable Time',
        tooltip:
          'Tag entries as billable or non-billable per client and project.',
        availability: { starter: true, grow: true, enterprise: true },
        subFeatures: [
          {
            key: 'billable-rates-per-user',
            label: 'Billable rates per user',
            tooltip:
              'Set individual hourly billing rates for each team member so invoices are calculated accurately per person.',
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'invoice-ready-reports',
            label: 'Invoice-ready reports',
            tooltip:
              'Generate a clean summary of all billable hours per client, ready to attach to or import into an invoice.',
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'client-billing-export',
            label: 'Client billing export',
            tooltip:
              'Export billing data in CSV, PDF, or Excel to import directly into your accounting or invoicing software.',
            availability: { starter: true, grow: true, enterprise: true },
          },
        ],
      },
    ],
  },
  {
    key: 'team-management',
    label: 'Team Management',
    features: [
      {
        key: 'user-roles-permissions',
        label: 'User Roles & Permissions',
        tooltip:
          'Granular role-based access to limit what each user can view or modify.',
        availability: { starter: true, grow: true, enterprise: true },
        subFeatures: [
          {
            key: 'admin-manager-member-roles',
            label: 'Admin / Manager / Member roles',
            tooltip:
              'Three built-in roles: Admins manage settings, Managers oversee teams, Members log their own time.',
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'custom-role-builder',
            label: 'Custom role builder',
            tooltip:
              'Create entirely custom roles with hand-picked permissions to fit unique organisational structures.',
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'department-level-permissions',
            label: 'Department-level permissions',
            tooltip:
              'Restrict data visibility so managers only see reports and timesheets for their own department.',
            availability: { starter: true, grow: true, enterprise: true },
          },
        ],
      },
      {
        key: 'team-hierarchy-departments',
        label: 'Team Hierarchy & Departments',
        tooltip:
          'Organise users into teams and departments for clearer reporting.',
        availability: { starter: false, grow: true, enterprise: true },
        subFeatures: [
          {
            key: 'multi-level-hierarchy',
            label: 'Multi-level hierarchy',
            tooltip:
              'Structure your organisation into divisions, departments, and sub-teams with parent-child relationships.',
            availability: { starter: false, grow: true, enterprise: true },
          },
          {
            key: 'department-dashboards',
            label: 'Department dashboards',
            tooltip:
              "Each department head gets a dedicated dashboard showing only their team's time, productivity, and attendance data.",
            availability: { starter: false, grow: true, enterprise: true },
          },
        ],
      },
      {
        key: 'leave-management',
        label: 'Leave Management',
        tooltip: 'Track and approve PTO, sick leave, and other absences.',
        availability: { starter: false, grow: true, enterprise: true },
        subFeatures: [
          {
            key: 'leave-type-configuration',
            label: 'Leave type configuration',
            tooltip:
              'Define your own leave types (Annual Leave, Sick Day, Unpaid Leave) with custom rules per type.',
            availability: { starter: false, grow: true, enterprise: true },
          },
          {
            key: 'approval-workflows-leave',
            label: 'Approval workflows',
            tooltip:
              'Leave requests are routed to the appropriate manager for approval, with automatic email notifications.',
            availability: { starter: false, grow: true, enterprise: true },
          },
          {
            key: 'balance-tracking',
            label: 'Balance tracking',
            tooltip:
              "Each user's remaining leave balance is calculated and displayed in real time as requests are approved.",
            availability: { starter: false, grow: true, enterprise: true },
          },
          {
            key: 'leave-calendar-view',
            label: 'Leave calendar view',
            tooltip:
              'A shared team calendar shows who is on leave on any given day, helping managers plan workloads.',
            availability: { starter: false, grow: true, enterprise: true },
          },
        ],
      },
      {
        key: 'shift-schedule-management',
        label: 'Shift & Schedule Management',
        tooltip: 'Define working hours, shift patterns, and on-call rotas.',
        availability: { starter: false, grow: true, enterprise: true },
        subFeatures: [
          {
            key: 'shift-templates',
            label: 'Shift templates',
            tooltip:
              'Create reusable shift templates (Morning, Evening, Night) and assign them to users or teams in bulk.',
            availability: { starter: false, grow: true, enterprise: true },
          },
          {
            key: 'schedule-calendar',
            label: 'Schedule calendar',
            tooltip:
              'A visual weekly and monthly calendar where managers can drag-and-drop shifts to plan team coverage.',
            availability: { starter: false, grow: true, enterprise: true },
          },
          {
            key: 'swap-request-workflows',
            label: 'Swap request workflows',
            tooltip:
              'Employees can request to swap shifts with colleagues; managers approve or reject swaps with one click.',
            availability: { starter: false, grow: true, enterprise: true },
          },
        ],
      },
      {
        key: 'timezone-management',
        label: 'Timezone Management',
        tooltip:
          'Supports globally distributed teams with timezone-aware time logging.',
        availability: { starter: true, grow: true, enterprise: true },
        subFeatures: [
          {
            key: 'per-user-timezone',
            label: 'Per-user timezone',
            tooltip:
              "Each user's time entries are recorded in their local timezone, eliminating confusion across global teams.",
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'utc-normalisation',
            label: 'UTC normalisation',
            tooltip:
              'All data is stored in UTC and converted on display, ensuring consistent reporting regardless of location.',
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'overlap-visibility',
            label: 'Overlap visibility',
            tooltip:
              'Managers can see which working hours overlap between team members in different timezones for scheduling.',
            availability: { starter: true, grow: true, enterprise: true },
          },
        ],
      },
    ],
  },
  {
    key: 'reports-analytics',
    label: 'Reports & Analytics',
    features: [
      {
        key: 'timesheets',
        label: 'Timesheets',
        tooltip:
          'Weekly/daily timesheets for all users with manager approval workflows.',
        availability: { starter: true, grow: true, enterprise: true },
        subFeatures: [
          {
            key: 'daily-weekly-view',
            label: 'Daily / weekly view',
            tooltip:
              'Switch between a day-by-day breakdown and a full weekly summary for any user or team.',
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'bulk-approval',
            label: 'Bulk approval',
            tooltip:
              'Managers can review and approve all pending timesheets for their team in a single click.',
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'export-to-pdf-excel',
            label: 'Export to PDF / Excel',
            tooltip:
              'Download any timesheet as a formatted PDF or Excel file for payroll processing or client billing.',
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'approval-workflow-timesheets',
            label: 'Approval workflow',
            tooltip:
              'Timesheets must be submitted by users and approved by managers before they are locked for payroll.',
            availability: { starter: true, grow: true, enterprise: true },
          },
        ],
      },
      {
        key: 'productivity-insights',
        label: 'Productivity Insights',
        tooltip:
          'AI-driven insights showing individual and team productivity trends.',
        availability: { starter: false, grow: true, enterprise: true },
        subFeatures: [
          {
            key: 'productivity-score',
            label: 'Productivity score',
            tooltip:
              'Each user is assigned a daily and weekly productivity score based on active time, task completion, and app usage.',
            availability: { starter: false, grow: true, enterprise: true },
          },
          {
            key: 'trend-charts',
            label: 'Trend charts',
            tooltip:
              'Visual line and bar charts show how productivity has changed over time, making it easy to spot patterns.',
            availability: { starter: false, grow: true, enterprise: true },
          },
          {
            key: 'peer-benchmarking',
            label: 'Peer benchmarking',
            tooltip:
              'Compare individual productivity scores against team averages to identify top performers and those who need support.',
            availability: { starter: false, grow: true, enterprise: true },
          },
        ],
      },
      {
        key: 'detailed-custom-reports',
        label: 'Detailed & Custom Reports',
        tooltip:
          'Build reports filtered by user, project, date range, client, and more.',
        availability: { starter: true, grow: true, enterprise: true },
        subFeatures: [
          {
            key: 'report-templates-15-plus',
            label: '15+ report templates',
            tooltip:
              'Choose from over 15 pre-built report templates covering time, attendance, productivity, projects, and billing.',
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'custom-date-ranges',
            label: 'Custom date ranges',
            tooltip:
              'Filter any report by an exact date range — today, this week, last month, or a custom start and end date.',
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'saved-presets',
            label: 'Saved presets',
            tooltip:
              'Save your most-used filter combinations as named presets so you can run the same report with one click.',
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'scheduled-email-delivery',
            label: 'Scheduled email delivery',
            tooltip:
              'Set reports to be automatically generated and emailed to stakeholders on a daily, weekly, or monthly schedule.',
            availability: { starter: true, grow: true, enterprise: true },
          },
        ],
      },
      {
        key: 'client-reports',
        label: 'Client Reports',
        tooltip:
          'White-label shareable reports for clients showing billed hours and progress.',
        availability: { starter: false, grow: true, enterprise: true },
        subFeatures: [
          {
            key: 'branded-report-links',
            label: 'Branded report links',
            tooltip:
              'Reports are served on a branded page with your company logo, giving clients a professional experience.',
            availability: { starter: false, grow: true, enterprise: true },
          },
          {
            key: 'project-summary-view',
            label: 'Project summary view',
            tooltip:
              'Clients see a high-level summary of hours logged, tasks completed, and budget consumed — no internal details exposed.',
            availability: { starter: false, grow: true, enterprise: true },
          },
          {
            key: 'secure-share-token',
            label: 'Secure share token',
            tooltip:
              'Each client report link uses a unique token that expires after a set time, keeping your data secure.',
            availability: { starter: false, grow: true, enterprise: true },
          },
        ],
      },
      {
        key: 'export-options',
        label: 'Export Options',
        tooltip:
          'Download or push data to third-party tools in multiple formats.',
        availability: { starter: true, grow: true, enterprise: true },
        subFeatures: [
          {
            key: 'csv-excel-pdf-export',
            label: 'CSV / Excel / PDF export',
            tooltip:
              'Export any report or dataset in CSV, Excel, or PDF format with a single click from the reports screen.',
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'api-data-access',
            label: 'API data access',
            tooltip:
              'Pull your Prodaff data programmatically via the REST API into your own dashboards or data warehouse.',
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'webhook-integrations-export',
            label: 'Webhook integrations',
            tooltip:
              'Subscribe to Prodaff events and receive instant HTTP POST payloads to your endpoint whenever something changes.',
            availability: { starter: true, grow: true, enterprise: true },
          },
        ],
      },
      {
        key: 'team-dashboards-visualisations',
        label: 'Team Dashboards & Visualisations',
        tooltip:
          'Real-time visual dashboards for managers to monitor team activity.',
        availability: { starter: false, grow: true, enterprise: true },
        subFeatures: [
          {
            key: 'live-activity-wall',
            label: 'Live activity wall',
            tooltip:
              'A real-time feed showing which team members are currently active, what they are working on, and for how long.',
            availability: { starter: false, grow: true, enterprise: true },
          },
          {
            key: 'heatmaps',
            label: 'Heatmaps',
            tooltip:
              'Visual heatmaps show peak productivity hours across your team, helping you optimise meetings and deadlines.',
            availability: { starter: false, grow: true, enterprise: true },
          },
          {
            key: 'custom-dashboard-widgets',
            label: 'Custom dashboard widgets',
            tooltip:
              'Drag-and-drop widgets onto your personal dashboard to surface the metrics that matter most to you.',
            availability: { starter: false, grow: true, enterprise: true },
          },
        ],
      },
    ],
  },
  {
    key: 'integrations',
    label: 'Integrations',
    features: [
      {
        key: 'custom-integrations-api',
        label: 'Custom Integrations & API',
        tooltip:
          'Full REST API access and webhook support for custom-built integrations.',
        availability: { starter: false, grow: true, enterprise: true },
        subFeatures: [
          {
            key: 'rest-api-access',
            label: 'REST API access',
            tooltip:
              'A fully documented REST API lets your developers read and write Prodaff data from any system or language.',
            availability: { starter: false, grow: true, enterprise: true },
          },
          {
            key: 'webhooks-integrations',
            label: 'Webhooks',
            tooltip:
              'Subscribe to Prodaff events and receive instant HTTP POST payloads to your endpoint whenever something changes.',
            availability: { starter: false, grow: true, enterprise: true },
          },
          {
            key: 'higher-api-rate-limits',
            label: 'Higher API rate limits',
            tooltip:
              'Enterprise plans get significantly higher API call limits per minute, supporting high-volume integrations.',
            availability: { starter: false, grow: true, enterprise: true },
          },
        ],
      },
      {
        key: 'payroll-accounting',
        label: 'Payroll & Accounting',
        tooltip:
          'Export payroll-ready reports to QuickBooks, Xero, and similar platforms.',
        availability: { starter: false, grow: false, enterprise: true },
        subFeatures: [
          {
            key: 'custom-payroll-csv',
            label: 'Custom payroll CSV',
            tooltip:
              'Generate a fully configurable payroll CSV with the exact columns your payroll software requires.',
            availability: { starter: false, grow: false, enterprise: true },
          },
        ],
      },
    ],
  },
  {
    key: 'security-compliance',
    label: 'Security & Compliance',
    features: [
      {
        key: 'data-encryption',
        label: 'Data Encryption',
        tooltip:
          'All data encrypted in transit (TLS 1.3) and at rest (AES-256).',
        availability: { starter: true, grow: true, enterprise: true },
        subFeatures: [
          {
            key: 'tls-1-3-in-transit',
            label: 'TLS 1.3 in transit',
            tooltip:
              'All data sent between your browser or app and Prodaff servers uses TLS 1.3, the latest encryption standard.',
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'aes-256-at-rest',
            label: 'AES-256 at rest',
            tooltip:
              'Every piece of stored data — timesheets, screenshots, reports — is encrypted at rest using AES-256 encryption.',
            availability: { starter: true, grow: true, enterprise: true },
          },
        ],
      },
      {
        key: 'gdpr-compliance',
        label: 'GDPR Compliance',
        tooltip: 'Tools and controls to meet GDPR requirements.',
        availability: { starter: true, grow: true, enterprise: true },
        subFeatures: [
          {
            key: 'data-export-on-request',
            label: 'Data export on request',
            tooltip:
              'Any user can request a full export of all their personal data held in Prodaff, fulfilling GDPR right of access.',
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'right-to-erasure',
            label: 'Right to erasure',
            tooltip:
              "Admins can permanently delete a user's personal data from Prodaff on request, per GDPR Article 17.",
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'dpa-available',
            label: 'DPA available',
            tooltip:
              'A Data Processing Agreement is available for organisations that require one for GDPR compliance.',
            availability: { starter: true, grow: true, enterprise: true },
          },
        ],
      },
      {
        key: 'sso-enterprise-identity',
        label: 'SSO & Enterprise Identity',
        tooltip:
          'Single Sign-On via SAML 2.0, Okta, Azure AD, and Google Workspace.',
        availability: { starter: false, grow: false, enterprise: true },
        subFeatures: [
          {
            key: 'saml-2-0',
            label: 'SAML 2.0',
            tooltip:
              'Prodaff supports the SAML 2.0 standard, enabling SSO with any identity provider that supports it.',
            availability: { starter: false, grow: false, enterprise: true },
          },
          {
            key: 'jit-provisioning',
            label: 'JIT provisioning',
            tooltip:
              'New user accounts are automatically created in Prodaff the first time a user logs in via SSO — no manual setup needed.',
            availability: { starter: false, grow: false, enterprise: true },
          },
        ],
      },
      {
        key: 'audit-logs',
        label: 'Audit Logs',
        tooltip: 'Full immutable audit trail of all admin and user actions.',
        availability: { starter: false, grow: false, enterprise: true },
        subFeatures: [
          {
            key: 'admin-action-logs',
            label: 'Admin action logs',
            tooltip:
              'Every admin action — permission changes, settings updates, user deletions — is logged with timestamp and actor identity.',
            availability: { starter: false, grow: false, enterprise: true },
          },
          {
            key: 'user-login-history',
            label: 'User login history',
            tooltip:
              'A full log of every login attempt, including timestamp, IP address, and success or failure status.',
            availability: { starter: false, grow: false, enterprise: true },
          },
          {
            key: 'export-audit-logs',
            label: 'Export audit logs',
            tooltip:
              'Audit log data can be exported as CSV or JSON for compliance reviews or security investigations.',
            availability: { starter: false, grow: false, enterprise: true },
          },
        ],
      },
    ],
  },
  {
    key: 'support-admin',
    label: 'Support & Admin',
    features: [
      {
        key: 'email-chat-support',
        label: 'Email & Chat Support',
        tooltip: 'Standard support via email ticketing and live in-app chat.',
        availability: { starter: true, grow: true, enterprise: true },
        subFeatures: [
          {
            key: 'response-time-24h',
            label: 'Response time: 24h',
            tooltip:
              'Starter plan support queries receive a response within 24 business hours via email or in-app chat.',
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'email-ticketing',
            label: 'Email ticketing',
            tooltip:
              'Submit support requests by email; they are tracked in a ticketing system so nothing gets lost.',
            availability: { starter: true, grow: true, enterprise: true },
          },
          {
            key: 'in-app-chat',
            label: 'In-app chat',
            tooltip:
              'Chat with the support team directly from within the Prodaff dashboard without leaving the product.',
            availability: { starter: true, grow: true, enterprise: true },
          },
        ],
      },
      {
        key: 'priority-support',
        label: 'Priority Support',
        tooltip:
          'Faster response times and dedicated support queues for Grow users.',
        availability: { starter: false, grow: true, enterprise: true },
        subFeatures: [
          {
            key: 'response-time-4h',
            label: 'Response time: 4h',
            tooltip:
              'Grow plan support queries are escalated to a priority queue with a guaranteed 4-hour response during business hours.',
            availability: { starter: false, grow: true, enterprise: true },
          },
          {
            key: 'dedicated-support-rep',
            label: 'Dedicated support rep',
            tooltip:
              'A named support representative is assigned to your account for consistent, personalised assistance.',
            availability: { starter: false, grow: true, enterprise: true },
          },
        ],
      },
      {
        key: 'dedicated-customer-success-manager',
        label: 'Dedicated Customer Success Manager',
        tooltip: 'A named CSM for onboarding, training, and ongoing guidance.',
        availability: { starter: false, grow: false, enterprise: true },
        subFeatures: [
          {
            key: 'onboarding-sessions',
            label: 'Onboarding sessions',
            tooltip:
              'Your CSM leads structured onboarding sessions to get your team fully set up and trained on Prodaff quickly.',
            availability: { starter: false, grow: false, enterprise: true },
          },
          {
            key: 'quarterly-reviews',
            label: 'Quarterly reviews',
            tooltip:
              'Regular quarterly business reviews with your CSM to assess usage, uncover issues, and plan future needs.',
            availability: { starter: false, grow: false, enterprise: true },
          },
          {
            key: 'escalation-path',
            label: 'Escalation path',
            tooltip:
              'Your CSM provides a direct escalation route to senior support and engineering for critical issues.',
            availability: { starter: false, grow: false, enterprise: true },
          },
        ],
      },
      {
        key: 'advanced-company-level-settings',
        label: 'Advanced Company-Level Settings',
        tooltip:
          'Company-wide policies for screenshots, idle sensitivity, and data retention.',
        availability: { starter: false, grow: true, enterprise: true },
        subFeatures: [
          {
            key: 'global-screenshot-policy',
            label: 'Global screenshot policy',
            tooltip:
              'Set screenshot frequency and blur rules once at company level so they apply uniformly to all users.',
            availability: { starter: false, grow: true, enterprise: true },
          },
          {
            key: 'data-retention-config',
            label: 'Data retention config',
            tooltip:
              'Define how long activity data, screenshots, and logs are retained before automatic deletion for compliance.',
            availability: { starter: false, grow: true, enterprise: true },
          },
          {
            key: 'company-branding',
            label: 'Company branding',
            tooltip:
              'Add your company logo and brand colours to the Prodaff interface and all exported reports.',
            availability: { starter: false, grow: true, enterprise: true },
          },
        ],
      },
      {
        key: 'sla-guarantee',
        label: 'SLA Guarantee',
        tooltip: 'Contractual 99.9% uptime SLA backed by credit policy.',
        availability: { starter: false, grow: false, enterprise: true },
        subFeatures: [
          {
            key: 'uptime-sla-99-9',
            label: '99.9% uptime SLA',
            tooltip:
              'A contractual guarantee that Prodaff will be available 99.9% of the time — less than 9 hours downtime per year.',
            availability: { starter: false, grow: false, enterprise: true },
          },
          {
            key: 'incident-credits',
            label: 'Incident credits',
            tooltip:
              'If uptime falls below the SLA threshold, affected customers receive account credits as compensation.',
            availability: { starter: false, grow: false, enterprise: true },
          },
        ],
      },
    ],
  },
];
